import { Input, Select, Checkbox } from '@dreamtree-org/twreact-ui';
import { customFieldsOf } from '@/lib/productTypes';

/**
 * The spec fields of one product type, generated from the registry (D32): no
 * per-type form code. `value` is the form (strings, booleans for yes/no),
 * `onChange(patch)` merges, `errors` is { key: message }.
 *
 * The store's own fields (ADR 0009 §4, MERCH-86: `type.custom_attributes`, see
 * lib/productTypes withCustom) follow the platform's, under "Your fields". Their
 * values are `value.custom`, their errors `custom.<key>`.
 */
export default function SpecFields({ type, value = {}, onChange, errors = {} }) {
  if (!type) return null;
  const own = customFieldsOf(type);
  const custom = (value && value.custom) || {};
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        {type.attributes.map((a) => (
          <Field key={a.key} a={a} v={value[a.key]} set={(v) => onChange({ [a.key]: v })} error={errors[a.key]} />
        ))}
      </div>
      {own.length ? (
        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold">Your fields</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            {own.map((a) => (
              <Field key={a.key} a={a} v={custom[a.key]} set={(v) => onChange({ custom: { ...custom, [a.key]: v } })} error={errors[`custom.${a.key}`]} />
            ))}
          </div>
        </fieldset>
      ) : null}
    </div>
  );
}

/** One attribute's control: yes/no a checkbox, a pick list (or suggested text) a select, else an input. */
function Field({ a, v, set, error }) {
  const label = a.unit && a.unit !== 'in' ? `${a.label} (${a.unit})` : a.unit === 'in' ? `${a.label} (inches)` : a.label;
  if (a.kind === 'boolean') {
    return (
      <div className="flex items-end pb-2">
        <Checkbox label={a.label} checked={!!v} onChange={(e) => set(e.target.checked)} />
      </div>
    );
  }
  if (a.kind === 'select' || (a.kind === 'text' && a.suggestions)) {
    const choices = a.kind === 'select' ? a.options : a.suggestions;
    const options = [...new Set([...choices, ...(v ? [v] : [])])].map((o) => ({ value: o, label: o }));
    return (
      <Select
        label={label}
        required={a.required}
        placeholder={a.placeholder || 'Choose'}
        options={options}
        value={v || ''}
        onChange={(next) => set(next || '')}
        searchable
        creatable={a.kind === 'text'}
        onCreateOption={a.kind === 'text' ? (text) => set(text) : undefined}
        allowClear={!a.required}
        closeOnSelect
        error={error}
      />
    );
  }
  return (
    <Input
      label={label}
      required={a.required}
      type={a.kind === 'number' ? 'number' : 'text'}
      maxLength={a.kind === 'text' ? a.maxLength || 120 : undefined}
      placeholder={a.placeholder}
      value={v == null ? '' : v}
      onChange={(e) => set(e.target.value)}
      error={error}
    />
  );
}
